import React, { useState, useEffect, useRef } from 'react';
import { Input, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { apiFetch } from '../utils/api.js';

// 方案A：Ctrl+K 命令面板（秒查数据 + 页面跳转）
const NAV_CMDS = [
  { k: '订单', v: '/orders' }, { k: '每日订单导入', v: '/import' }, { k: '库存管理', v: '/inventory' },
  { k: '每日入库', v: '/daily-inbound' }, { k: '采购管理', v: '/purchase' }, { k: '售后管理', v: '/aftersales' },
  { k: '财务分析', v: '/finance' }, { k: '商品成本', v: '/products' }, { k: '对账中心', v: '/reconcile' },
  { k: '运费模板', v: '/freight' }, { k: '区域分析', v: '/region' }, { k: '用户管理', v: '/users' },
  { k: 'AI 助手', v: '/ai' }, { k: '首页', v: '/' },
];
const AI_CMDS = [
  { k: '今日概况', q: '今日经营概况如何' }, { k: '补货建议', q: '哪些产品需要补货，建议补多少' },
  { k: '补货', q: '哪些产品需要补货，建议补多少' }, { k: '超卖预警', q: '发货超卖预警分析' },
  { k: '超卖', q: '发货超卖预警分析' }, { k: '快递对账', q: '快递对账情况有什么差异' },
  { k: '对账', q: '快递对账情况有什么差异' }, { k: '退款分析', q: '退款原因归类分析' },
  { k: '退款', q: '退款原因归类分析' }, { k: '库存滞留', q: '帮我分析库存滞留产品' },
  { k: '滞留', q: '帮我分析库存滞留产品' }, { k: '库存', q: '当前库存情况' },
  { k: '商品优化', q: '商品结构优化建议' }, { k: '周报', q: '生成近7天周报数据' },
  { k: '月报', q: '生成这个月的月报数据' }, { k: '利润', q: '本月利润情况' },
  { k: '销售', q: '分析最近的销售情况' }, { k: '排行', q: '销售排行' },
];
const ALL = [...AI_CMDS.map(x => ({ ...x, type: 'ai' })), ...NAV_CMDS.map(x => ({ ...x, type: 'nav' }))];

export default function CommandPalette({ onNavigate }) {
  const [open, setOpen] = useState(false);
  const [kw, setKw] = useState('');
  const [sel, setSel] = useState(0);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen(!open); setKw(''); setSel(0); setResult(null); }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open]);
  useEffect(() => { if (open && inputRef.current) setTimeout(() => inputRef.current.focus(), 50); }, [open]);

  const list = ALL.filter(c => c.k.includes(kw.trim()) || kw.trim().includes(c.k)).slice(0, 10);

  const run = async (cmd) => {
    setResult(null); setBusy(true);
    if (cmd.type === 'nav') { if (onNavigate) onNavigate(cmd.v); setOpen(false); setBusy(false); return; }
    try {
      const r = await apiFetch('/api/ai/chat', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ messages: [{ role: 'user', content: cmd.q }] }) }).then(x => x.json());
      setResult(r.success ? r.reply : ('出错了：' + (r.error || '')));
    } catch (e) { setResult('网络异常，请稍后重试'); }
    setBusy(false);
  };

  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '16vh' }} onClick={() => setOpen(false)}>
      <div style={{ width: 520, maxWidth: '92vw', background: '#fff', borderRadius: 14, boxShadow: '0 16px 48px rgba(0,0,0,.25)', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid var(--brand-border)' }}>
          <SearchOutlined style={{ color: 'var(--brand)', fontSize: 16 }} />
          <Input bordered={false} ref={inputRef} value={kw} onChange={e => { setKw(e.target.value); setSel(0); }}
            placeholder="输入：补货 / 对账 / 库存 / 订单 / 财务…" style={{ flex: 1, fontSize: 15 }} />
          <span style={{ fontSize: 11, color: '#bfbfbf', border: '1px solid #E5DCD5', borderRadius: 4, padding: '1px 6px' }}>ESC</span>
        </div>
        <div style={{ maxHeight: 320, overflowY: 'auto', padding: 6 }}>
          {kw.trim() && list.length === 0 && <div style={{ padding: 16, textAlign: 'center', color: '#999', fontSize: 13 }}>无匹配命令</div>}
          {list.map((c, i) => (
            <div key={c.k} onMouseEnter={() => setSel(i)} onClick={() => run(c)}
              style={{ padding: '9px 14px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, background: i === sel ? '#FAEEDA' : 'transparent' }}>
              <span style={{ fontSize: 13, color: i === sel ? 'var(--brand-strong)' : '#26201C' }}>{c.k}</span>
              <span style={{ fontSize: 11, color: '#999', marginLeft: 'auto' }}>{c.type === 'nav' ? '跳转页面' : '智能分析'}</span>
            </div>
          ))}
          {!kw.trim() && <div style={{ padding: '10px 14px', color: '#bfbfbf', fontSize: 12 }}>按 Ctrl+K 关闭 · 输入关键词快速查询或跳转</div>}
        </div>
        {busy && <div style={{ padding: '10px 16px', borderTop: '1px solid var(--brand-border)', color: '#8c8c8c', fontSize: 13 }}><Spin size="small" /> 智能助手分析中…</div>}
        {result && (
          <div style={{ maxHeight: 260, overflowY: 'auto', padding: '10px 16px', borderTop: '1px solid var(--brand-border)', background: 'var(--brand-bg)' }}>
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 13, lineHeight: 1.7, margin: 0, color: '#26201C' }}>{result}</pre>
          </div>
        )}
      </div>
    </div>
  );
}
